/**
 * Product Image Zoom Modal
 * Tuned for themes using:
 *   product-gallery-grid > .product-gallery-item > img   (desktop)
 *   product-gallery-slider .swiper-slide > .product-gallery-item > img  (mobile)
 */

(function () {
  'use strict';

  const CSS_CLASS = {
    modal:       'pzm-modal',
    open:        'pzm-modal--open',
    thumbList:   'pzm-thumbs',
    thumbItem:   'pzm-thumb',
    thumbActive: 'pzm-thumb--active',
    mainWrap:    'pzm-main-wrap',
    mainImg:     'pzm-main-img',
    zoomed:      'pzm-main-img--zoomed',
    dragging:    'pzm-main-img--dragging',
    btnClose:    'pzm-btn-close',
    btnPrev:     'pzm-btn-prev',
    btnNext:     'pzm-btn-next',
    counter:     'pzm-counter',
    overlay:     'pzm-overlay',
  };

  let modal, mainWrap, mainImg, thumbList, btnClose, btnPrev, btnNext, counter;
  let images = [];
  let currentIndex = 0;
  let isZoomed = false;

  // Drag state
  let isDragging  = false;
  let dragStartX  = 0, dragStartY  = 0;
  let translateX  = 0, translateY  = 0;
  let lastTranslateX = 0, lastTranslateY = 0;

  // ─── Build Modal DOM ───────────────────────────────────────────────────────
  function buildModal() {
    if (document.getElementById('product-zoom-modal')) {
      // already built — just grab references
      modal     = document.getElementById('product-zoom-modal');
      thumbList = modal.querySelector('.' + CSS_CLASS.thumbList);
      mainWrap  = modal.querySelector('.' + CSS_CLASS.mainWrap);
      mainImg   = modal.querySelector('.' + CSS_CLASS.mainImg);
      btnClose  = modal.querySelector('.' + CSS_CLASS.btnClose);
      btnPrev   = modal.querySelector('.' + CSS_CLASS.btnPrev);
      btnNext   = modal.querySelector('.' + CSS_CLASS.btnNext);
      counter   = modal.querySelector('.' + CSS_CLASS.counter);
      return;
    }

    modal = document.createElement('div');
    modal.id = 'product-zoom-modal';
    modal.className = CSS_CLASS.modal;
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');
    modal.setAttribute('aria-label', 'Product image zoom');

    const overlay = document.createElement('div');
    overlay.className = CSS_CLASS.overlay;
    overlay.addEventListener('click', close);

    thumbList = document.createElement('ul');
    thumbList.className = CSS_CLASS.thumbList;
    thumbList.setAttribute('role', 'listbox');

    mainWrap = document.createElement('div');
    mainWrap.className = CSS_CLASS.mainWrap;

    mainImg = document.createElement('img');
    mainImg.className = CSS_CLASS.mainImg;
    mainImg.alt = '';
    mainImg.draggable = false;
    mainWrap.appendChild(mainImg);

    btnPrev = document.createElement('button');
    btnPrev.className = CSS_CLASS.btnPrev;
    btnPrev.setAttribute('aria-label', 'Previous image');
    btnPrev.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"/></svg>';

    btnNext = document.createElement('button');
    btnNext.className = CSS_CLASS.btnNext;
    btnNext.setAttribute('aria-label', 'Next image');
    btnNext.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg>';

    btnClose = document.createElement('button');
    btnClose.className = CSS_CLASS.btnClose;
    btnClose.setAttribute('aria-label', 'Close');
    btnClose.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>';

    counter = document.createElement('div');
    counter.className = CSS_CLASS.counter;

    modal.appendChild(overlay);
    modal.appendChild(thumbList);
    modal.appendChild(mainWrap);
    modal.appendChild(btnPrev);
    modal.appendChild(btnNext);
    modal.appendChild(btnClose);
    modal.appendChild(counter);
    document.body.appendChild(modal);

    // Events
    btnClose.addEventListener('click', close);
    btnPrev.addEventListener('click', function () { navigate(-1); });
    btnNext.addEventListener('click', function () { navigate(1); });

    mainImg.addEventListener('click', function (e) {
      if (isDragging) return;
      toggleZoom(e);
    });

    mainImg.addEventListener('mousedown', onDragStart);
    window.addEventListener('mousemove', onDragMove);
    window.addEventListener('mouseup', onDragEnd);

    mainImg.addEventListener('touchstart', onTouchStart, { passive: true });
    window.addEventListener('touchmove', onTouchMove, { passive: false });
    window.addEventListener('touchend', onTouchEnd);

    document.addEventListener('keydown', onKeydown);
  }

  // ─── Collect images from YOUR theme's HTML ─────────────────────────────────
  // Structure:
  //   Desktop: product-gallery-grid  .product-gallery-item img
  //   Mobile:  product-gallery-slider .swiper-slide .product-gallery-item img
  //            (mobile has duplicates — deduplicate by base filename)
  function collectImages() {
    const sources = [];
    const seen    = new Set();

    // Prefer desktop grid (no duplicates, correct order)
    // Falls back to slider if grid not present
    const grid   = document.querySelector('product-gallery-grid');
    const slider = document.querySelector('product-gallery-slider');
    const source = grid || slider;

    if (!source) return sources;

    source.querySelectorAll('.product-gallery-item img').forEach(function (img) {
      // Build the highest-quality URL from the srcset or src
      const fullUrl = bestSrc(img);
      const key     = baseFilename(fullUrl);

      if (key && !seen.has(key)) {
        seen.add(key);
        sources.push({
          full:  fullUrl,
          thumb: thumbSrc(img),
          alt:   img.alt || '',
        });
      }
    });

    return sources;
  }

  // Pick the largest srcset entry, or fall back to src
  function bestSrc(img) {
    if (img.srcset) {
      var parts = img.srcset.split(',').map(function (s) { return s.trim(); });
      var best  = { w: 0, url: '' };
      parts.forEach(function (part) {
        var tokens = part.split(/\s+/);
        var url    = tokens[0];
        var w      = tokens[1] ? parseInt(tokens[1]) : 0;
        if (w > best.w) { best = { w: w, url: url }; }
      });
      if (best.url) {
        // Ask Shopify for a 1800px version of that file
        return best.url.replace(/(\?v=\d+).*$/, '$1&width=1800');
      }
    }
    // src: strip width param and request 1800px
    return img.src.replace(/&width=\d+/, '').replace(/\?width=\d+/, '') + (img.src.includes('?') ? '&width=1800' : '?width=1800');
  }

  // Pick a ~150px thumbnail from srcset, or resize via URL
  function thumbSrc(img) {
    if (img.srcset) {
      var parts = img.srcset.split(',').map(function (s) { return s.trim(); });
      // Find the 150w entry, or the smallest available
      var candidates = parts.map(function (p) {
        var t = p.split(/\s+/);
        return { url: t[0], w: t[1] ? parseInt(t[1]) : 0 };
      }).filter(function (c) { return c.w > 0; });
      candidates.sort(function (a, b) { return a.w - b.w; });
      var pick = candidates.find(function (c) { return c.w >= 120; }) || candidates[0];
      if (pick) return pick.url;
    }
    return img.src.replace(/&width=\d+/, '').replace(/\?width=\d+/, '') + (img.src.includes('?') ? '&width=150' : '?width=150');
  }

  // Extract base filename for dedup (e.g. "20250929-032.jpg")
  function baseFilename(url) {
    try {
      var path = url.split('?')[0];
      return path.split('/').pop();
    } catch (e) {
      return url;
    }
  }

  // ─── Open / Close ──────────────────────────────────────────────────────────
  function open(index) {
    images = collectImages();
    if (!images.length) return;

    buildModal();
    rebuildThumbs();

    currentIndex = Math.max(0, Math.min(index, images.length - 1));
    showImage(currentIndex);

    modal.classList.add(CSS_CLASS.open);
    document.body.style.overflow = 'hidden';
    touchMoved = true;
  }

  function close() {
    if (!modal) return;
    modal.classList.remove(CSS_CLASS.open);
    document.body.style.overflow = '';
    resetZoom();
  }

  // ─── Thumbnails ────────────────────────────────────────────────────────────
  function rebuildThumbs() {
    thumbList.innerHTML = '';
    images.forEach(function (img, i) {
      var li = document.createElement('li');
      li.className = CSS_CLASS.thumbItem;
      li.setAttribute('role', 'option');
      li.setAttribute('aria-selected', 'false');
      li.tabIndex = 0;

      var t = document.createElement('img');
      t.src     = img.thumb;
      t.alt     = img.alt || ('Image ' + (i + 1));
      t.loading = 'lazy';

      li.appendChild(t);
      li.addEventListener('click', (function (idx) {
        return function () { showImage(idx); };
      })(i));
      li.addEventListener('keydown', function (e) {
        if (e.key === 'Enter') showImage(i);
      });

      thumbList.appendChild(li);
    });
  }

  // ─── Show image ────────────────────────────────────────────────────────────
  function showImage(index) {
    currentIndex = index;
    resetZoom();

    mainImg.src = images[index].full;
    mainImg.alt = images[index].alt || ('Product image ' + (index + 1));
    counter.textContent = (index + 1) + ' / ' + images.length;

    var thumbItems = thumbList.querySelectorAll('.' + CSS_CLASS.thumbItem);
    thumbItems.forEach(function (li, i) {
      li.classList.toggle(CSS_CLASS.thumbActive, i === index);
      li.setAttribute('aria-selected', i === index ? 'true' : 'false');
    });
    if (thumbItems[index]) {
      thumbItems[index].scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }

    btnPrev.disabled = false;
btnNext.disabled = false;
  }

  function navigate(dir) {
  var next = currentIndex + dir;

  // Loop to first/last image
  if (next >= images.length) {
    next = 0;
  }

  if (next < 0) {
    next = images.length - 1;
  }

  showImage(next);
}

  // ─── Zoom ──────────────────────────────────────────────────────────────────
  var ZOOM_SCALE = 3;

  function toggleZoom(e) {
    if (!isZoomed) {
      var rect = mainImg.getBoundingClientRect();
      var cx   = (e.clientX - rect.left)  / rect.width;
      var cy   = (e.clientY - rect.top)   / rect.height;
      translateX = (0.5 - cx) * rect.width  * (ZOOM_SCALE - 1);
      translateY = (0.5 - cy) * rect.height * (ZOOM_SCALE - 1);
      lastTranslateX = translateX;
      lastTranslateY = translateY;
      isZoomed = true;
      mainImg.classList.add(CSS_CLASS.zoomed);
      applyTransform();
    } else {
      resetZoom();
    }
  }

  function resetZoom() {
    isZoomed       = false;
    isDragging     = false;
    translateX     = 0; translateY     = 0;
    lastTranslateX = 0; lastTranslateY = 0;
    if (mainImg) {
      mainImg.classList.remove(CSS_CLASS.zoomed, CSS_CLASS.dragging);
      mainImg.style.transform = '';
    }
  }

  function applyTransform() {
    mainImg.style.transform =
      'scale(' + ZOOM_SCALE + ') translate(' +
      (translateX / ZOOM_SCALE) + 'px, ' +
      (translateY / ZOOM_SCALE) + 'px)';
  }

  // ─── Mouse drag ────────────────────────────────────────────────────────────
  var hasMoved = false;

  function onDragStart(e) {
    if (!isZoomed) return;
    isDragging = true;
    hasMoved   = false;
    dragStartX = e.clientX - lastTranslateX;
    dragStartY = e.clientY - lastTranslateY;
    mainImg.classList.add(CSS_CLASS.dragging);
    e.preventDefault();
  }

  function onDragMove(e) {
    if (!isDragging || !isZoomed) return;
    hasMoved   = true;
    translateX = e.clientX - dragStartX;
    translateY = e.clientY - dragStartY;
    clampTranslate();
    applyTransform();
  }

  function onDragEnd() {
    if (!isDragging) return;
    isDragging     = hasMoved;
    lastTranslateX = translateX;
    lastTranslateY = translateY;
    mainImg.classList.remove(CSS_CLASS.dragging);
    setTimeout(function () { isDragging = false; }, 10);
  }

  // ─── Touch drag ────────────────────────────────────────────────────────────
  var touchStartX = 0, touchStartY = 0;
  var touchLastX  = 0, touchLastY  = 0;
  var touchMoved  = false;
  let justOpened = false;
  let isPinching = false;
  let pinchOccurred = false;

  function onTouchStart(e) {
     if (e.touches.length > 1) {
    isPinching = true;
    pinchOccurred = true;
    return;
  }
    if (e.touches.length !== 1) return;
    touchStartX = e.touches[0].clientX;
    touchStartY = e.touches[0].clientY;
    touchLastX  = e.touches[0].clientX;
    touchLastY  = e.touches[0].clientY;
    // Do NOT reset touchMoved here — open() may have just set it to true
    // to block the zoom-on-open bug. Let onTouchEnd consume it first.
    if (!touchMoved) touchMoved = false;
}

  function onTouchMove(e) {
    if (e.touches.length > 1) {
    isPinching = true;
    pinchOccurred = true;
    return;
  }

  if (isPinching) return;
    if (e.touches.length !== 1) return;
    var dx = Math.abs(e.touches[0].clientX - touchLastX);
    var dy = Math.abs(e.touches[0].clientY - touchLastY);

    if (isZoomed) {
      e.preventDefault();
      touchMoved = true;
      translateX = e.touches[0].clientX - touchStartX;
      translateY = e.touches[0].clientY - touchStartY;
      clampTranslate();
      applyTransform();
    } else {
      if (dx > dy && dx > 10) touchMoved = true;
    }

    touchLastX = e.touches[0].clientX;
    touchLastY = e.touches[0].clientY;
  }


  function onTouchEnd(e) {

  if (pinchOccurred) {

    if (!e.touches || e.touches.length === 0) {
      isPinching = false;

      setTimeout(function () {
        pinchOccurred = false;
      }, 50);
    }

    return;
  }
  if (!modal || !modal.classList.contains('pzm-modal--open')) return;

  // TAP = zoom in/out
  if (!touchMoved) {

    if (!isZoomed) {
      toggleZoom({
        clientX: touchLastX,
        clientY: touchLastY
      });
    } else {
      resetZoom();
    }

    return;
  }

  // SWIPE ONLY WHEN NOT ZOOMED
  if (!isZoomed && !pinchOccurred) {

  var deltaX = touchLastX - touchStartX;

  if (Math.abs(deltaX) > 50) {

    if (deltaX < 0) {
      navigate(1);
    } else {
      navigate(-1);
    }
  }
}

  // SAVE POSITION AFTER DRAG
  if (isZoomed) {
    lastTranslateX = translateX;
    lastTranslateY = translateY;
  }
  if (!e.touches || e.touches.length === 0) {
  isPinching = false;
  pinchOccurred = false;
}
}

  // ─── Clamp ─────────────────────────────────────────────────────────────────
  function clampTranslate() {
    var rect = mainImg.getBoundingClientRect();
    var maxX = (rect.width  * (ZOOM_SCALE - 1)) / 2;
    var maxY = (rect.height * (ZOOM_SCALE - 1)) / 2;
    translateX = Math.max(-maxX, Math.min(maxX, translateX));
    translateY = Math.max(-maxY, Math.min(maxY, translateY));
  }

  // ─── Keyboard ──────────────────────────────────────────────────────────────
  function onKeydown(e) {
    if (!modal || !modal.classList.contains(CSS_CLASS.open)) return;
    if (e.key === 'Escape')     close();
    if (e.key === 'ArrowLeft')  navigate(-1);
    if (e.key === 'ArrowRight') navigate(1);
  }

  // ─── Attach click triggers to YOUR theme's gallery items ──────────────────
  // Desktop: product-gallery-grid .product-gallery-item
  // Mobile:  product-gallery-slider .swiper-slide  (each has one .product-gallery-item)
  function attachTriggers() {
    // --- Desktop grid ---
    var gridItems = document.querySelectorAll('product-gallery-grid .product-gallery-item');
    gridItems.forEach(function (el, i) {
      if (el.dataset.pzmBound) return;
      el.dataset.pzmBound = '1';
      el.style.cursor = 'zoom-in';
      el.addEventListener('click', function (e) {
        e.preventDefault();
        open(i);
      });
    });

    // --- Mobile slider slides ---
    // Use the swiper-slide index so clicking slide 2 opens image 2
    var sliderSlides = document.querySelectorAll('product-gallery-slider .swiper-slide');
    sliderSlides.forEach(function (slide, i) {
      if (slide.dataset.pzmBound) return;
      slide.dataset.pzmBound = '1';
      slide.style.cursor = 'zoom-in';
      var slideTouch = { x: 0, y: 0, time: 0 };
      slide.addEventListener('touchstart', function (e) {
        slideTouch.x = e.touches[0].clientX;
        slideTouch.y = e.touches[0].clientY;
        slideTouch.time = Date.now();
      }, { passive: true });

      slide.addEventListener('click', function (e) {
        e.preventDefault();
        open(i);
      });
    });
  }

  // ─── Public API ────────────────────────────────────────────────────────────
  window.ProductZoomModal = {
    init: function () {
      buildModal();
      attachTriggers();
    },
    open:  open,
    close: close,
  };

  // Auto-init
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', window.ProductZoomModal.init);
  } else {
    window.ProductZoomModal.init();
  }

  // Re-init after Shopify variant/section changes
  document.addEventListener('shopify:section:load', window.ProductZoomModal.init);

})();